/**
 * Sub-region filter chips (BON-534).
 *
 * The design's mobile regional view puts these directly under the breadcrumb:
 * a coloured dot per sub-region, matching the accent its workshops carry in the
 * list, so tapping one narrows the list to that territory.
 *
 * Selecting the chip that is already on clears the filter, which is how a
 * single-select chip row is expected to behave on touch — there is no separate
 * "all" chip to hunt for.
 */
export interface RegionFilterOption {
  label: string;
  color: string;
}

export interface RegionFilterChipsProps {
  options: RegionFilterOption[];
  /** The label currently filtered to, or null for everything. */
  selected: string | null;
  onSelect: (label: string | null) => void;
}

export function RegionFilterChips({ options, selected, onSelect }: RegionFilterChipsProps) {
  return (
    <div
      role="group"
      aria-label="Filter by sub-region"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
    >
      {options.map((option) => {
        const on = selected === option.label;
        return (
          <button
            key={option.label}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(on ? null : option.label)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '6px 12px',
              borderRadius: 999,
              cursor: 'pointer',
              border: `1px solid ${on ? 'var(--nb-primary)' : 'var(--nb-border)'}`,
              background: on ? 'var(--nb-primary-soft)' : 'var(--nb-bg)',
              color: 'var(--nb-ink)',
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <i
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                background: option.color,
                flexShrink: 0,
              }}
            />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
